import { FormattedMessage, FormattedPlural } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from '@xing-com/button';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { SMALL_SCREEN_BOTTOM_SHEET_PARAM } from '@xing-com/crate-jobs-constants';
import { useSearchTracking } from '@xing-com/crate-jobs-tracking';
import { dropShadowS, spaceXL } from '@xing-com/tokens';

import { useSearch } from '../../../hooks';
import { LimitedJobCount } from '../../shared';
import type { Views } from '../filter-bottom-sheet/filter-switcher';

type Props = {
  activeView: Views;
  isSecondLevel: boolean;
  trackPage: () => void;
  isNewSearch: boolean;
};

export const ShowResultsButton: React.FC<Props> = ({
  activeView,
  isSecondLevel,
  trackPage,
  isNewSearch,
}) => {
  const { deleteSearchParameter } = useSearchParameter();
  const { isLoading, results } = useSearch();
  const { trackFilterClose } = useSearchTracking();

  const action = isNewSearch ? 'apply' : 'close';
  const withShadow =
    activeView === 'discipline' || activeView === 'all' || isSecondLevel;

  const handleOnClick = (): void => {
    const filter = activeView.startsWith('all') ? 'all_filters' : activeView;

    deleteSearchParameter(SMALL_SCREEN_BOTTOM_SHEET_PARAM);
    trackFilterClose(filter, action);
    trackPage();
  };

  // Give priority to the bounded total values, in case there's any
  const jobCount = results?.boundedTotal?.total ?? results?.total ?? 0;
  const limit = results?.boundedTotal?.bound
    ? results.boundedTotal.bound - 1
    : undefined;

  const pluralLocale =
    jobCount === 0
      ? 'JOBS_SEARCH_FILTERS_SHOW_RESULTS_BUTTON.zero'
      : 'JOBS_SEARCH_FILTERS_SHOW_RESULTS_BUTTON.other';

  return (
    <>
      <StyledButton
        loading={isLoading}
        variant={isNewSearch ? 'primary' : 'tertiary'}
        onClick={handleOnClick}
        $withShadow={withShadow}
        data-testid={`show-results-button-${action}`}
      >
        {results?.total === -1 && !results.boundedTotal ? (
          <FormattedMessage id="JOBS_SEARCH_AI_FILTERS_SHOW_RESULTS_BUTTON" />
        ) : (
          <FormattedPlural
            value={jobCount}
            one={
              <FormattedMessage id="JOBS_SEARCH_FILTERS_SHOW_RESULTS_BUTTON.one" />
            }
            other={
              <FormattedMessage
                id={pluralLocale}
                values={{
                  variable_2: (
                    <LimitedJobCount count={jobCount} limit={limit} />
                  ),
                }}
              />
            }
          />
        )}
      </StyledButton>
    </>
  );
};

const StyledButton = styled(Button)<{ $withShadow?: boolean }>`
  position: sticky;
  margin: 0 auto;
  bottom: ${spaceXL};

  ${({ $withShadow }) =>
    $withShadow &&
    css`
      box-shadow: ${dropShadowS};
    `}
`;
